import {
	BackdropCircularProgress,
	DeleteConfirmationDialog,
	EnhancedDialog,
	EnhancedSnackbar,
	IResponse,
	IServerError,
	SingleColumnLayout,
	StandardPanel,
} from "@websential/cosmic-v20";
import { FormProvider, useForm } from "react-hook-form";
import { Grid, useTheme } from "@mui/material";
import {
	INITIAL_PAGE_SIZE,
	INITIAL_SNACKBAR_DATA,
	ROWS_PER_PAGE_PER_OPTIONS,
} from "../../common/constants";
import { Link, useNavigate, useParams } from "react-router-dom";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { DDLDataTable } from "../DDLDataTable";
import { EnhancedButtonPrimeReact } from "../EnhancedButtonPrimeReact";
import { IAppState } from "../../store/reducers";
import { IGetListRequest } from "../../models";
import { IReimbursements } from "../../models/Reimbursements";
import { ReimbursementsInfoSetupFieldsPanel } from "./Panels/ReimbursementsInfoSetupFieldsPanel";
import { ReimbursementsService } from "../../services/ReimbursementsService";
import { ReimbursementsSetupToolbar } from "./ReimbursementsSetupToolbar";
import { ReimbursementsSetupValidationSchema } from "./validator";
import { setSelectedDrawerOptionsMenu } from "../../store/actions/drawerMenuOptionsActions";
import { setSelectedStickyNavBar } from "../../store/actions";
import { useNavigateToLogout } from "../../common/Common";
import { yupResolver } from "@hookform/resolvers/yup";

export interface IReimbursementsSetup { }
const SINGLE_RECORD_DATA_QUERY_KEY = "reimbursements-single-data-record";

const useGetData = (
	clientId: string,
	id: string | undefined,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: [SINGLE_RECORD_DATA_QUERY_KEY, clientId, id, navigateToLogout],
		queryFn: () => {
			return ReimbursementsService.getById(clientId, id, navigateToLogout);
		},
		refetchOnWindowFocus: false,
	});

const REIMBERSEMENT_LIST_QUERY_KEY = "reimbursements-list";

const useReimbursements = (
	clientId: string,
	params: IGetListRequest,
	navigateToLogout: (path: string) => void
) =>
	useQuery({
		queryKey: ["reimbursements-list", params],
		queryFn: (data) => {

			return ReimbursementsService.getReimbursements(
				clientId,
				params,
				navigateToLogout
			);
		},
		refetchOnWindowFocus: false,
	});

const useGetReimbursementsDDLItems = (clientId: string) =>
	useQuery({
		queryKey: ['Reimbursements-ddl', clientId],
		queryFn: (data) => {
			return ReimbursementsService.getReimbursementDDL(clientId);
		},
		refetchOnWindowFocus: false,
	});
export const ReimbursementsSetup: React.FC<IReimbursementsSetup> = () => {
	const navigate = useNavigate();
	const dispatch = useDispatch();
	const drawerMenuOptions = useSelector(
		(state: IAppState) => state.drawerMenu.menu
	);
	const theme = useTheme();
	const subMenuVisibility = useSelector(
		(state: IAppState) => state.drawerAction.subMenuVisibility
	);
	const clientId = useSelector((state: IAppState) => state.client.id);
	const navigateToLogout = useNavigateToLogout();
	const [openDialog, setDialogOpen] = useState(false);
	const [openArchiveDialog, setArchiveDialogOpen] = useState(false);
	const [singleDelete, setSingleDelete] = useState<boolean>(false);
	const [singleArchive, setSingleArchive] = useState<boolean>(false);
	const [reimbursementsId, setReimbursementsId] = useState<number>(0);
	const [snackbarData, setSnackbarData] = useState(INITIAL_SNACKBAR_DATA);
	const queryClient = useQueryClient();
	const { id } = useParams();

	const [editRecordId, setEditRecordId] = useState(id);

	const [queryParams, setQueryParams] = useState<IGetListRequest>({
		search_query: "",
		order_by: "label",
		order_direction: "asc",
		page: 1,
		size: 25,
	});

	const onRequestParamsChange = useCallback((requestParams: any) => {
		let dispatchParams = {
			search_query: requestParams.searchQuery ? requestParams.searchQuery : "",
			order_by:
				requestParams.sortField !== null ? requestParams.sortField : "label",
			order_direction: requestParams.sortOrder
				? requestParams.sortOrder
				: "asc",
			page: requestParams.page + 1,
			size: requestParams.rows,
		};

		setQueryParams(dispatchParams);
	}, []);

	const { isPending, data } = useReimbursements(
		clientId,
		queryParams,
		navigateToLogout
	);

	const reimbursementsList: any | null = data;



	const { isPending: isPendingReimbursementsDDLItems, data: reimbursementsDDLItems } =
		useGetReimbursementsDDLItems(clientId);

	const { isPending: isSubmitting, mutate: submitReimbursementsFormFields } =
		useMutation({
			mutationFn: ReimbursementsService.create,
			onSuccess: (res: IReimbursements | IServerError | IResponse | any) => {

				if (res.status === 200) {
					// if (instanceOfIReimbursementsResponse(res)) {
					// setEditRecordId(res.id);
					// queryClient.invalidateQueries(SINGLE_RECORD_DATA_QUERY_KEY);
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					queryClient.invalidateQueries({ queryKey: [SINGLE_RECORD_DATA_QUERY_KEY] });
					queryClient.invalidateQueries({ queryKey: [REIMBERSEMENT_LIST_QUERY_KEY] });
					let path = `/company/reimbursements/setup/${res.data.id}`;
					navigate(path);
					// Notify other tabs
					localStorage.setItem('reimbursements', Date.now().toString());

				} else if (res.status === 201) {
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
					// }
					queryClient.invalidateQueries({ queryKey: [SINGLE_RECORD_DATA_QUERY_KEY] });
					queryClient.invalidateQueries({ queryKey: [REIMBERSEMENT_LIST_QUERY_KEY] });
					setEditRecordId(res.data.id)
					let path = `/company/reimbursements/setup/${res.data.id}`;
					navigate(path);
					queryClient.invalidateQueries({
						queryKey: [SINGLE_RECORD_DATA_QUERY_KEY],
					});
					queryClient.invalidateQueries({
						queryKey: [REIMBERSEMENT_LIST_QUERY_KEY],
					});
					// Notify other tabs
					localStorage.setItem('reimbursements', Date.now().toString());
				} else {
					setSnackbarData({
						open: true,
						message: res.message ? res.message : "Something went wrong!",
						severity: "error",
					});
				}
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const { isPending: isPendingById, data: selectedDataRecord } = useGetData(
		clientId,
		editRecordId,
		navigateToLogout
	);

	const upadtedReimbursementsListItems = useMemo(() => {
		return (
			reimbursementsList &&
			reimbursementsList.items &&
			reimbursementsList?.items.map((item: any) => ({
				...item,
				label: (
					<Link
						to={`/company/reimbursements/setup/${item.id}`}
						className="no-underline"
						style={{ color: theme.palette.primary.light }}
						onClick={() => setEditRecordId(item.id)}
					>
						{item.label}
					</Link>
				),
			}))
		);
	}, [reimbursementsList]);

	const onReimbursementsSelectionChangeHandler = (selected: any) => {
		if (selected.value) {
			setEditRecordId(selected.value)
			navigate(`/company/reimbursements/setup/${selected.value}`)
		} else {
			setEditRecordId(undefined);
			formMethods.reset();
			navigate(`/company/reimbursements/setup`);
		}
	}

	const DEFAULT_VALUES = {
		label: undefined,
		showInPayroll: 200,
		maxYtd: "0.00",
		t4Code: undefined,
		rl1Code: undefined,
	};

	const formMethods = useForm<any>({
		resolver: yupResolver(ReimbursementsSetupValidationSchema),
		mode: "onBlur",
		reValidateMode: "onChange",
		defaultValues: DEFAULT_VALUES
	});

	const onSubmit = (values: any) => {

		let payload = {
			clientId: clientId,
			id: id && id,
			navigateToLogout: navigateToLogout,
			label: values.label,
			showInPayroll: values.showInPayroll,
			maxYtd:
				values.maxYtd &&
				values.maxYtd !== "" &&
				parseFloat(values.maxYtd.replace(/[$,]/g, "")),
			t4Code: values.t4Code,
			rl1Code: values.rl1Code,
		};
		submitReimbursementsFormFields(payload);
	};

	useEffect(() => {
		if (id && selectedDataRecord) {

			Object.keys(selectedDataRecord).forEach((fieldName) => {
				const fieldValue = selectedDataRecord[fieldName];
				formMethods.setValue(fieldName, fieldValue ?? undefined);
				if (fieldName === "maxYtd") {
					formMethods.setValue(
						fieldName,
						fieldValue && fieldValue !== null
							? parseFloat(fieldValue).toFixed(2)
							: "0.00"
					);
				}
			});
		}
	}, [id, selectedDataRecord]);

	const onSnackbarClosed = (
		_event: React.SyntheticEvent | Event,
		reason?: string
	) => {
		if (reason === "clickaway") {
			return;
		}
		setSnackbarData(INITIAL_SNACKBAR_DATA);
	};

	const { isPending: isDeleting, mutate: deleteReimbursementsById } =
		useMutation({
			mutationFn: ReimbursementsService.delReimbursements,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the Reimbursements fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/company/reimbursements/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [REIMBERSEMENT_LIST_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const deleteReimbursementsField = () => {
		if (singleDelete) {
			setSingleDelete(false);
			let delPayload = {
				reimbursementsId: reimbursementsId,
				navigateToLogout: navigateToLogout,
			};
			deleteReimbursementsById(delPayload);
			setDialogOpen(false);
		}
	};

	const onDeleteClicked = () => {
		setDialogOpen(true);
		setSingleDelete(true);
		if (selectedDataRecord) {
			setReimbursementsId(selectedDataRecord.id);
		}
	};

	const onRowDeleteClicked = useCallback((params: any) => {
		setDialogOpen(true);
		setSingleDelete(true);
		setReimbursementsId(params.id);
	}, []);

	const { isPending: isArchive, mutate: archiveReimbursementsById } =
		useMutation({
			mutationFn: ReimbursementsService.archiveReimbursements,
			onSuccess: (res: string | IServerError | IResponse) => {
				if (typeof res === "string") {
					// remove the cache of the Reimbursements fields since we just added a new field.
					setSnackbarData({
						open: true,
						message: "Success",
						severity: "success",
					});
				} else {
					setSnackbarData({
						open: true,
						message: "Something went wrong, try again later!",
						severity: "error",
					});
				}
				formMethods.reset();
				let path = `/company/reimbursements/setup`;
				navigate(path);
				return queryClient.invalidateQueries({
					queryKey: [REIMBERSEMENT_LIST_QUERY_KEY],
				});
			},
			onError: () => {
				// TODO: customize the toast message based on the response
				setSnackbarData({
					open: true,
					message: "Something went wrong, try again later!",
					severity: "error",
				});
			},
		});

	const archiveReimbursementsField = () => {
		if (singleArchive) {
			setSingleArchive(false);
			let ArchivePayload = {
				reimbursementsId: reimbursementsId,
				navigateToLogout: navigateToLogout,
			};
			archiveReimbursementsById(ArchivePayload);
			setArchiveDialogOpen(false);
		}
	};

	const onArchiveClicked = () => {
		setArchiveDialogOpen(true);
		setSingleArchive(true);
		if (selectedDataRecord) {
			setReimbursementsId(selectedDataRecord.id);
		}
	};

	const onCloseHandler = () => {
		setDialogOpen(false);
	};
	const onCloseArchiveHandler = () => {
		setArchiveDialogOpen(false);
	};

	const onResetClicked = () => {
		setSnackbarData({
			open: true,
			message: "Success",
			severity: "success",
		});
		formMethods.reset();
		let path = `/company/reimbursements/setup`;
		navigate(path);
	};

	const onRowEditClicked = useCallback((params: IReimbursements | any) => {
		// alert(`Edit row with ID: ${params.row.id} has been clicked!`);
		let path = `/company/reimbursements/setup/${params.value}`;
		navigate(path);
	}, []);

	const expandedLabels = ["Company", "EDR", "Reimbursements"];
	const expandedReimbursementsSetupOrSetupItem = "Reimbursements"; // Special case for "Setup" under "City Tax"
	const enhanceData = (data: any[], parentLabel?: string): any[] => {
		return data.map((item) => {
			let isExpanded = expandedLabels.includes(item.label);
			if (
				parentLabel === "EDR" &&
				item.label === expandedReimbursementsSetupOrSetupItem
			) {
				isExpanded = true;
			}
			const enhancedItem = {
				...item,
				expanded: item.items ? isExpanded : false,
				items: item.items ? enhanceData(item.items, item.label) : undefined,
				path: item.label === expandedReimbursementsSetupOrSetupItem && "/company/reimbursements/setup",
			};
			return enhancedItem;
		});
	};

	useEffect(() => {
		// dispatch(
		// 	setSelectedActivityLogActions({
		// 		entityId1: "",
		// 		entityId2: "",
		// 		entityId3: "",
		// 		screenName: ACCOUNTANT_SETUP_SCREEN_SLUG,
		// 		reimbursementsName: "",
		// 		activityType:
		// 			"activity_type=create&activity_type=updated&activity_type=delete",
		// 		activityLogVisibility: true,
		// 	})
		// );
		dispatch(setSelectedStickyNavBar({ isSticky: true }));
		if (drawerMenuOptions && drawerMenuOptions.length > 0) {
			const enhancedData = enhanceData(drawerMenuOptions);
			dispatch(setSelectedDrawerOptionsMenu({ menu: enhancedData }));
		}
	}, [drawerMenuOptions]);

	return (
		<Grid
		// sx={{
		// 	pb: "5rem",
		// 	"& .MuiAppBar-root": {
		// 		zIndex: 2,
		// 		top: subMenuVisibility ? "154px" : "112px",
		// 	},
		// }}
		>
			{" "}
			<BackdropCircularProgress
				open={isSubmitting || isPendingById || isDeleting || isArchive || isPendingReimbursementsDDLItems}
				testId="comapny Setup"
			/>
			<DeleteConfirmationDialog
				open={openDialog}
				deleteActionHandler={deleteReimbursementsField}
				cancelActionHandler={onCloseHandler}
				testId="reimbursements-confirmation-dialog"
			/>
			<FormProvider {...formMethods}>
				<form onSubmit={formMethods.handleSubmit(onSubmit)}>
					<Grid container size={{ xs: 12, sm: 12, md: 12, lg: 12, xl: 12 }}>
						<Grid size={{ xs: 12, sm: 12, md: 5, lg: 4.5, xl: 3.5 }}>
							<SingleColumnLayout
								children={
									<StandardPanel
										title="Reimbursements"
										testId={`reimbursements`}
										showTitle={false}
										variant="outlined"
										topPadding={2}
									>
										<ReimbursementsInfoSetupFieldsPanel

											reimbursementsDDLItems={reimbursementsDDLItems} onReimbursementsSelectionChangeHandler={onReimbursementsSelectionChangeHandler} />

									</StandardPanel>
								}
								toolbar={
									<ReimbursementsSetupToolbar
										onResetClicked={onResetClicked}
										onDeleteClicked={onDeleteClicked}
										isSubmitting={false}
										id={id}
									/>
								}
								testId="form-layout"
								showFullDiaplay={true}
								toolbarPosition="bottom"
							/>
						</Grid>
						<Grid size={{ xs: 12, sm: 12, md: 2, lg: 3.4, xl: 5.4 }}></Grid>
						<Grid
							size={{ xs: 12, sm: 12, md: 5, lg: 4.1, xl: 3.1 }}
							sx={{ marginTop: "16px", display: { xs: "none", sm: "none", md: "block" } }}
						>
							<DDLDataTable
								rows={
									reimbursementsList && upadtedReimbursementsListItems
										? upadtedReimbursementsListItems
										: []
								}
								onRowEditClicked={onRowEditClicked}
								onRowDeleteClicked={onRowDeleteClicked}
								onRowArchiveClicked={onArchiveClicked}
								isLoading={isPending}
								totalRecords={
									reimbursementsList && reimbursementsList.total
										? reimbursementsList.total
										: 0
								}
								initialPageSize={INITIAL_PAGE_SIZE}
								rowsPerPageOptions={ROWS_PER_PAGE_PER_OPTIONS}
								onRequestParamsChange={onRequestParamsChange}
								tableScrollHeight={"60vh"}
								selectedId={selectedDataRecord && id && selectedDataRecord.id}
							/>
						</Grid>
						<Grid size={{ xs: 0, sm: 0, md: 0, lg: 1, xl: 1 }}></Grid>
					</Grid>
				</form>
			</FormProvider>
			{/* Archive Dialogue */}
			<EnhancedDialog
				dialogTitle="Please Confirm"
				key="dialog-box"
				testId="dialog"
				onCloseHandler={onCloseArchiveHandler}
				open={openArchiveDialog}
				dialogContent={null}
				showdividers={false}
				dialogAction={
					<Grid>
						<EnhancedButtonPrimeReact
							label="Cancel"
							severity="secondary"
							text
							raised
							onClickHandler={onCloseArchiveHandler}
							style={{ fontSize: "small" }}
						/>
						<EnhancedButtonPrimeReact
							label="Archive"
							severity="secondary"
							raised
							onClickHandler={() => archiveReimbursementsField}
							style={{ marginLeft: "16px", fontSize: "small" }}
						/>
					</Grid>
				}
			/>
			<EnhancedSnackbar
				message={snackbarData.message}
				onCloseHandler={onSnackbarClosed}
				severity={snackbarData.severity}
				testId={"reimbursements-snackbar"}
				open={snackbarData.open}
			/>
		</Grid>
	);
};
